import { useState } from "react"
import FormErrors from "components/formParts/formErrors"
import { useTranslation } from "react-i18next"
import { ifPasswordIsSecure } from "utils/validation"
import { decodeFirebaseError } from "utils/firebaseHelpers"
import TextInput from "components/inputs/textInput"
import PasswordInput from "components/inputs/passwordInput"
import Button from "components/inputs/button"
import ButtonGroup from "components/buttons/buttonGroup"
import ProcessingButton from "components/inputs/processingButton"
import useLayout from "hooks/useLayout"
import useAuth from "hooks/useAuth"
import Form from "components/forms/form"
import { ReactComponent as SignUpIcon } from "assets/icons/group_add.svg"
import Section from "@/components/layout/section"
import { InputMaxLength } from "types"

interface SignUpFormProps {
    acceptedTerms: boolean
}

const SignUpForm = ({ acceptedTerms }: SignUpFormProps) => {

    const { t } = useTranslation()

    // Form data.
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')

    // Form status.
    const [errors, setErrors] = useState<string[]>([])

    // Auth hooks.
    const { signUp, isSigningUp } = useAuth()

    // Layout hooks.
    const { addToast } = useLayout()

    // Validate the form.
    const validate = () => {
        setErrors([])
        // Check password match.
        if (password !== confirmPassword) {
            setErrors([t('passwordsDoNotMatch')])
            return false
        }
        // Check if password is secure.
        if (!ifPasswordIsSecure(password)) {
            setErrors([t('weakPassword')])
            return false
        }
        if (!acceptedTerms) {
            setErrors([t('errorAcceptTerms')])
            return false
        }
        return true
    }

    // Handle submit.
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!validate()) return
        setErrors([])
        
        try {
            await signUp({ email, password });
            addToast({ text: t('toastSignUpSuccess'), colorScheme: 'green' })
        } catch (error: any) {
            const errorMessage = decodeFirebaseError(error?.code || error?.message || 'Unknown error')
            setErrors([errorMessage])
            addToast({ text: t('toastSignUpError'), colorScheme: 'red' })
        }
    }

    // Handle email change.
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value)
    }

    // Handle password change.
    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value)
    }

    // Handle confirm password change.
    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmPassword(e.target.value)
    }

    // Return the form.
    return (
        <Form
            className={`group`}
            onSubmit={handleSubmit}
            isLoading={isSigningUp}
        >
            <Section>
                <TextInput label={t('email')} value={email} onChange={handleEmailChange} autoComplete={`email`} required maxLength={InputMaxLength.medium} />
                <PasswordInput label={t('password')} value={password} onChange={handlePasswordChange} autoComplete={`new-password`} required />
                <PasswordInput label={t('confirmPassword')} value={confirmPassword} onChange={handleConfirmPasswordChange} autoComplete={`confirm-password`} required />
            </Section>
            <Section>
                <ButtonGroup>
                    {isSigningUp ? (
                        <ProcessingButton design="primary" />
                    ) : (
                        <Button wideOnMobile type="submit" design="primary"><SignUpIcon /><span>{t('buttonSignUp')}</span></Button>
                    )}
                </ButtonGroup>
                {errors.length > 0 && <FormErrors errors={errors} />}
            </Section>
        </Form>
    )
}

export default SignUpForm
